import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Plug, Plus, X, RefreshCw, Trash2, Edit2, Play, CheckCircle2,
  AlertTriangle, Activity, Clock, EyeOff, Cable, Database,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'

interface ConfigField {
  key: string
  label: string
  type: string
  required: boolean
  placeholder: string
  help: string
  options: string[] | null
  secret: boolean
}

interface Kind {
  kind: string
  display_name: string
  vendor: string
  category: string
  icon: string
  description: string
  config_fields: ConfigField[]
}

interface Connector {
  id: number
  kind: string
  name: string
  vendor: string
  category: string
  config: Record<string, any>
  enabled: boolean
  last_run_at: string | null
  last_status: string
  last_error: string
  last_summary: any
  last_duration_ms: number
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'never'
  const d = (Date.now() - new Date(iso).getTime()) / 1000
  if (d < 60) return `${Math.floor(d)}s`
  if (d < 3600) return `${Math.floor(d / 60)}m`
  if (d < 86400) return `${Math.floor(d / 3600)}h`
  return `${Math.floor(d / 86400)}d`
}

function CDMBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border bg-purple-500/10 border-purple-500/40 text-purple-300"
      title="Feeds the CDM event log; queryable via /app/cdm-correlations and /app/attack-paths."
    >
      <Database size={9} /> CDM
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'ok' ? 'bg-emerald/10 border-emerald/30 text-emerald'
    : status === 'error' ? 'bg-rose/10 border-rose/30 text-rose'
    : status === 'running' ? 'bg-blue/10 border-blue/30 text-blue-light'
    : 'bg-surface-raised border-border text-text-muted'
  const icon = status === 'ok' ? <CheckCircle2 size={10} />
    : status === 'error' ? <AlertTriangle size={10} />
    : status === 'running' ? <Activity size={10} />
    : <Clock size={10} />
  return (
    <span className={clsx('inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border capitalize', cls)}>
      {icon} {status}
    </span>
  )
}

function ConnectorForm({
  kinds, initial, onSave, onCancel, onTestLive,
}: {
  kinds: Kind[]
  initial?: Partial<Connector>
  onSave: (c: any) => void
  onCancel: () => void
  onTestLive?: (c: any) => Promise<{ ok: boolean; message: string }>
}) {
  const [kind, setKind] = useState(initial?.kind || kinds[0]?.kind || '')
  const [name, setName] = useState(initial?.name || '')
  const [category, setCategory] = useState(initial?.category || 'other')
  const [config, setConfig] = useState<Record<string, any>>(initial?.config || {})
  const [enabled, setEnabled] = useState(initial?.enabled !== false)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [testing, setTesting] = useState(false)

  const currentKind = useMemo(() => kinds.find(k => k.kind === kind), [kind, kinds])

  useEffect(() => {
    if (currentKind && !name) setName(currentKind.display_name)
    if (currentKind && !initial?.category) setCategory(currentKind.category)
  }, [currentKind, name, initial])

  const updateField = (key: string, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }))
  }

  const runTest = async () => {
    if (!onTestLive) return
    setTesting(true)
    try {
      const r = await onTestLive({ kind, name, category, config, enabled })
      setTestResult(r)
    } catch (e: any) {
      setTestResult({ ok: false, message: e.message || 'Test failed' })
    } finally { setTesting(false) }
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Kind *</label>
          <select value={kind} onChange={e => { setKind(e.target.value); setConfig({}) }} title="Kind" className="input py-1.5 text-[12px] w-full" disabled={!!initial?.id}>
            {kinds.map(k => (
              <option key={k.kind} value={k.kind}>{k.display_name} — {k.vendor}</option>
            ))}
          </select>
          {currentKind && <p className="text-[9px] text-text-muted mt-1">{currentKind.description}</p>}
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Display name *</label>
          <input value={name} onChange={e => setName(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="e.g. CrowdStrike Production" />
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Category</label>
        <select value={category} onChange={e => setCategory(e.target.value)} title="Category" className="input py-1.5 text-[12px] w-full">
          {['security', 'endpoint', 'itsm', 'cloud', 'observability', 'productivity', 'cost', 'other'].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {currentKind && currentKind.config_fields.length > 0 && (
        <div className="space-y-2 border-t border-border pt-3">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Configuration</div>
          {currentKind.config_fields.map(f => (
            <div key={f.key}>
              <label className="block text-[10px] text-text-secondary mb-1">
                {f.label}{f.required && <span className="text-rose"> *</span>}
              </label>
              {f.type === 'select' && f.options ? (
                <select value={config[f.key] || ''} onChange={e => updateField(f.key, e.target.value)} title={f.label} className="input py-1.5 text-[12px] w-full">
                  <option value="">—</option>
                  {f.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : f.type === 'json' ? (
                <textarea
                  value={typeof config[f.key] === 'string' ? config[f.key] : JSON.stringify(config[f.key] ?? '', null, 2)}
                  onChange={e => updateField(f.key, e.target.value)}
                  rows={4}
                  className="input py-1.5 text-[11px] w-full font-mono"
                  placeholder={f.placeholder || '{}'}
                />
              ) : (
                <input
                  type={f.type === 'password' || f.secret ? 'password' : f.type === 'number' ? 'number' : 'text'}
                  value={config[f.key] ?? ''}
                  onChange={e => updateField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="input py-1.5 text-[12px] w-full"
                  autoComplete="off"
                />
              )}
              {f.help && <p className="text-[9px] text-text-muted mt-0.5">{f.help}</p>}
            </div>
          ))}
        </div>
      )}

      <label className="flex items-center gap-2 text-[11px] text-text-secondary cursor-pointer pt-1">
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="accent-blue" />
        Enabled (included in Executive Summary + auto-refresh)
      </label>

      {testResult && (
        <div className={clsx('text-[10px] p-2 rounded border',
          testResult.ok
            ? 'bg-emerald/5 border-emerald/30 text-emerald'
            : 'bg-rose/5 border-rose/30 text-rose')}>
          {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button type="button" disabled={!kind || !name} onClick={() => onSave({ kind, name, category, config, enabled })} className="btn-primary text-xs">
          Save
        </button>
        {initial?.id && onTestLive && (
          <button type="button" onClick={runTest} disabled={testing} className="btn-secondary text-xs">
            <Play size={11} /> {testing ? 'Testing…' : 'Test connection'}
          </button>
        )}
        <button type="button" onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function ConnectorsManagePage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [connectors, setConnectors] = useState<Connector[]>([])
  const [kinds, setKinds] = useState<Kind[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Connector | null>(null)
  const [prefillKind, setPrefillKind] = useState<string | null>(null)
  // CDM filter chip — when on, only connectors whose kind starts with 'cdm.' are shown.
  const [cdmOnly, setCdmOnly] = useState(false)
  const isCDMKind = (k: string) => k.startsWith('cdm.')
  const visibleConnectors = useMemo(
    () => cdmOnly ? connectors.filter(c => isCDMKind(c.kind)) : connectors,
    [connectors, cdmOnly],
  )
  const visibleKinds = useMemo(
    () => cdmOnly ? kinds.filter(k => isCDMKind(k.kind)) : kinds,
    [kinds, cdmOnly],
  )
  const cdmCount = useMemo(() => kinds.filter(k => isCDMKind(k.kind)).length, [kinds])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [{ data: list }, { data: k }] = await Promise.all([
        api.get('/api/connectors'),
        api.get('/api/connectors/kinds'),
      ])
      setConnectors(list.connectors)
      setKinds(k.kinds)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Auto-open the edit form if the URL has ?edit=<id> — e.g. when arriving from the Executive Summary
  useEffect(() => {
    const editId = searchParams.get('edit')
    if (editId && connectors.length > 0 && !showForm) {
      const target = connectors.find(c => String(c.id) === editId)
      if (target) {
        setEditing(target)
        setShowForm(true)
        // Clear the query param so a later back-nav doesn't reopen
        setSearchParams({}, { replace: true })
      }
    }
  }, [searchParams, connectors, showForm, setSearchParams])

  const save = async (payload: any) => {
    if (editing) await api.put(`/api/connectors/${editing.id}`, payload)
    else         await api.post('/api/connectors', payload)
    setShowForm(false); setEditing(null); setPrefillKind(null)
    fetchAll()
  }

  const remove = async (id: number) => {
    await api.delete(`/api/connectors/${id}`)
    fetchAll()
  }

  const trigger = async (id: number) => {
    await api.post(`/api/connectors/${id}/trigger`)
    fetchAll()
  }

  const testLive = async (payload: any) => {
    // Save then test if new. For edit, just test with what's stored.
    if (editing?.id) {
      const { data } = await api.post(`/api/connectors/${editing.id}/test`)
      return data
    }
    // Unsaved — create temporary and test then delete
    const { data: created } = await api.post('/api/connectors', payload)
    try {
      const { data } = await api.post(`/api/connectors/${created.id}/test`)
      return data
    } finally {
      await api.delete(`/api/connectors/${created.id}`)
    }
  }

  if (loading && !kinds.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Tip banner — point admins at the unified surface. Non-blocking;
          this page keeps working as the legacy CRUD over the connectors
          table. */}
      <div className="card p-2.5 border-blue/30 bg-blue/5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[11px] text-text-secondary">
          <Cable size={14} className="text-blue-light" />
          <span>
            <b className="text-text-primary">Tip:</b>{' '}
            <code className="px-1 py-0.5 rounded bg-surface-raised text-blue-light">/app/admin/feeds</code>{' '}
            is the unified surface for all integration types — feeds, CDM
            adapters, webhooks, vendor connectors. The new home going forward.
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate('/app/admin/feeds')}
          className="btn-ghost text-[10px]"
        >
          Open unified feeds
        </button>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Plug size={20} className="text-blue" /> Connectors — Manage
          </h1>
          <p className="text-[10px] text-text-secondary">
            {connectors.length} configured · {kinds.length} plugin kinds available.
            Changes flow to the <button type="button" onClick={() => navigate('/app/executive')} className="text-blue-light hover:underline">Executive Summary</button>.
            <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald/10 text-emerald border border-emerald/30 text-[9px] font-mono font-semibold">UI v4.2 · click-row to edit</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCdmOnly(v => !v)}
            className={clsx(
              'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded border transition-colors',
              cdmOnly
                ? 'bg-purple-500/15 border-purple-500/50 text-purple-200'
                : 'bg-surface-raised border-border text-text-muted hover:border-purple-500/40 hover:text-purple-300',
            )}
            title="Show only CDM-flavoured connectors (those that feed the CDM event log)"
            aria-pressed={cdmOnly ? 'true' : 'false'}
          >
            <Database size={11} /> CDM only{cdmCount ? ` (${cdmCount})` : ''}
          </button>
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs" title="Refresh list" aria-label="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <button type="button" onClick={() => { setEditing(null); setShowForm(true) }} className="btn-primary text-xs">
            <Plus size={12} /> Add connector
          </button>
        </div>
      </div>

      {/* Configured vs awaiting-creds summary across all available connector kinds */}
      <div className="card p-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Configured</div>
            <div className="text-2xl font-bold text-emerald font-mono">{connectors.filter(c => c.enabled).length}</div>
            <div className="text-[9px] text-text-muted">of {kinds.length} possible</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Last run OK</div>
            <div className="text-2xl font-bold text-emerald-light font-mono">{connectors.filter(c => c.last_status === 'ok').length}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Errored</div>
            <div className="text-2xl font-bold text-rose-light font-mono">{connectors.filter(c => c.last_status === 'error').length}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Awaiting creds</div>
            <div className="text-2xl font-bold text-amber-light font-mono">{kinds.length - connectors.length}</div>
            <div className="text-[9px] text-text-muted">unconfigured kinds — click <b>Add connector</b></div>
          </div>
        </div>
      </div>

      {visibleConnectors.length === 0 ? (
        <div className="card p-8 text-center">
          <Cable size={32} className="text-text-muted mx-auto mb-2" />
          <div className="text-sm font-semibold text-text-primary mb-1">No connectors yet</div>
          <p className="text-[11px] text-text-secondary mb-3">
            Start with the <b>Demo</b> kind to see the layout with synthetic data, or <b>HTTP JSON</b> to wire any tool with a REST API.
          </p>
          <button type="button" onClick={() => { setEditing(null); setShowForm(true) }} className="btn-primary text-xs">
            <Plus size={12} /> Add your first
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-surface-raised/50">
              <tr className="border-b border-border">
                <th className="text-left text-[10px] text-text-muted font-semibold py-2 px-3">Name</th>
                <th className="text-left text-[10px] text-text-muted font-semibold py-2 px-3">Kind</th>
                <th className="text-left text-[10px] text-text-muted font-semibold py-2 px-3">Category</th>
                <th className="text-left text-[10px] text-text-muted font-semibold py-2 px-3">Status</th>
                <th className="text-right text-[10px] text-text-muted font-semibold py-2 px-3">Last run</th>
                <th className="text-right text-[10px] text-text-muted font-semibold py-2 px-3 w-40" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visibleConnectors.map(c => (
                <tr
                  key={c.id}
                  onClick={() => { setEditing(c); setShowForm(true) }}
                  className={clsx(
                    'border-b-2 border-border/50 cursor-pointer transition-colors',
                    'hover:bg-blue/5 hover:border-blue/30',
                    !c.enabled && 'opacity-50',
                  )}
                  title="Click anywhere on this row to edit"
                >
                  <td className="py-2 px-3">
                    <div className="text-[11px] font-semibold text-text-primary flex items-center gap-1.5 flex-wrap">
                      <span>{c.name}</span>
                      {isCDMKind(c.kind) && <CDMBadge />}
                    </div>
                    <div className="text-[9px] text-text-muted">{c.vendor}</div>
                  </td>
                  <td className="py-2 px-3 text-[10px] font-mono text-text-secondary">{c.kind}</td>
                  <td className="py-2 px-3 text-[10px] text-text-muted capitalize">{c.category}</td>
                  <td className="py-2 px-3"><StatusBadge status={c.last_status} /></td>
                  <td className="py-2 px-3 text-right text-[10px] font-mono text-text-muted">
                    {timeAgo(c.last_run_at)} {c.last_duration_ms ? `· ${c.last_duration_ms}ms` : ''}
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); trigger(c.id) }}
                      className="btn-ghost px-1.5 py-1 text-[10px] text-blue-light inline-flex items-center gap-1"
                      title="Trigger now"
                    >
                      <Play size={11} /> Run
                    </button>
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); setEditing(c); setShowForm(true) }}
                      className="btn-primary px-3 py-1.5 text-[11px] inline-flex items-center gap-1.5 ml-1 font-semibold"
                      title="Edit this connector"
                    >
                      <Edit2 size={12} /> EDIT
                    </button>
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); if (confirm(`Delete ${c.name}?`)) remove(c.id) }}
                      className="btn-ghost px-1.5 py-1 text-[10px] text-rose inline-flex items-center gap-1 ml-1"
                      title="Delete"
                    >
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Available kinds — click to add a new connector of that kind */}
      <div className="card p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Available plugin kinds</div>
          <span className="text-[10px] text-text-secondary">Click any card to add a new connector of that kind</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {visibleKinds.map(k => (
            <button
              type="button"
              key={k.kind}
              onClick={() => {
                setEditing(null)
                setPrefillKind(k.kind)
                setShowForm(true)
              }}
              className="border border-border rounded p-2 text-left hover:border-blue/40 hover:bg-blue/5 hover:shadow-md transition-all cursor-pointer group"
              title={`Add a new ${k.display_name} connector`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold text-text-primary truncate flex items-center gap-1.5">
                  {k.display_name} {isCDMKind(k.kind) && <CDMBadge />}
                </span>
                <span className="text-[9px] text-text-muted uppercase tracking-wider shrink-0">{k.category}</span>
              </div>
              <div className="text-[9px] text-text-muted">{k.vendor}</div>
              <p className="text-[10px] text-text-secondary mt-1 line-clamp-2">{k.description}</p>
              <div className="mt-1.5 text-[9px] text-blue-light opacity-0 group-hover:opacity-100 transition-opacity">
                + Add {k.display_name} →
              </div>
            </button>
          ))}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
              <h2 className="text-sm font-bold text-text-primary">
                {editing ? `Edit ${editing.name}` : 'Add connector'}
              </h2>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); setPrefillKind(null) }} title="Close" aria-label="Close" className="text-text-muted hover:text-text-primary">
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <ConnectorForm
                kinds={kinds}
                initial={editing || (prefillKind ? ({ kind: prefillKind } as Partial<Connector>) : undefined)}
                onSave={save}
                onCancel={() => { setShowForm(false); setEditing(null); setPrefillKind(null) }}
                onTestLive={testLive}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
